import { useState, useEffect, useMemo } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Search, Users, TrendingUp, Award } from 'lucide-react';
import { toast } from 'sonner';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { DateRangePicker } from '@/components/DateRangePicker';
import { ExportButton } from '@/components/ExportButton';
import { GeoFilters } from '@/components/GeoFilters';

const formatINR = (v) => {
  if (v >= 10000000) return `${(v / 10000000).toFixed(1)}Cr`;
  if (v >= 100000) return `${(v / 100000).toFixed(1)}L`;
  if (v >= 1000) return `${(v / 1000).toFixed(1)}K`;
  return v?.toLocaleString('en-IN') || '0';
};

function ChartTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="chart-tooltip">
      <p className="text-xs font-heading font-semibold text-foreground mb-1">{label}</p>
      {payload.map((p, i) => (
        <p key={i} className="text-xs text-muted-foreground">
          <span className="inline-block w-2 h-2 rounded-full mr-1.5" style={{ background: p.color }} />
          {p.name}: {typeof p.value === 'number' ? p.value.toLocaleString('en-IN') : p.value}
        </p>
      ))}
    </div>
  );
}

export default function PerformancePage() {
  const { api } = useAuth();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ employees: [], kpis: {} });
  const [zone, setZone] = useState('');
  const [subzone, setSubzone] = useState('');
  const [state, setState] = useState('');
  const [city, setCity] = useState('');
  const [search, setSearch] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  // The optional second range. Set it and every figure on the page gains the same figure for
  // that period beside it.
  const [baseFrom, setBaseFrom] = useState('');
  const [baseTo, setBaseTo] = useState('');
  const [sortBy, setSortBy] = useState('amount');

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const params = {};
        if (zone) params.zone = zone;
        if (subzone) params.subzone = subzone;
        if (state) params.state = state;
        if (city) params.city = city;
        if (search) params.search = search;
        if (dateFrom) params.date_from = dateFrom;
        if (dateTo) params.date_to = dateTo;
        if (baseFrom && baseTo) { params.base_from = baseFrom; params.base_to = baseTo; }
        const res = await api.get('/performance', { params });
        setData(res.data);
      } catch (err) {
        toast.error('Failed to load performance data');
      } finally {
        setLoading(false);
      }
    };
    const t = setTimeout(load, search ? 300 : 0);
    return () => clearTimeout(t);
  }, [api, zone, subzone, state, city, search, dateFrom, dateTo, baseFrom, baseTo]);

  const k = data.kpis || {};
  const comparing = !!data.compared;
  const money = (n) => `INR ${formatINR(n || 0)}`;

  // Against a base period the leaderboard can be read two ways: who sold the most, or who moved
  // the most. The second is usually why somebody turned the comparison on.
  const rows = useMemo(() => {
    const list = [...(data.employees || [])];
    if (comparing && sortBy === 'movers') {
      list.sort((a, b) => Math.abs((b.order_amount || 0) - (b.base?.order_amount || 0))
                        - Math.abs((a.order_amount || 0) - (a.base?.order_amount || 0)));
    } else {
      list.sort((a, b) => (b.order_amount || 0) - (a.order_amount || 0));
    }
    return list;
  }, [data.employees, comparing, sortBy]);

  const topPerformers = rows.slice(0, 10);

  // A change worth colouring. Nothing from nothing is not a rise.
  const Delta = ({ now, was, pct }) => {
    const d = (now || 0) - (was || 0);
    if (!was && !now) return <span className="text-muted-foreground">&ndash;</span>;
    const up = d >= 0;
    return (
      <span className={up ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'}>
        {up ? '+' : '\u2212'}{formatINR(Math.abs(d))}
        {pct !== null && pct !== undefined && <span className="ml-1 opacity-70">({up ? '+' : ''}{pct}%)</span>}
      </span>
    );
  };

  return (
    <div className="space-y-6" data-testid="performance-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-heading font-bold tracking-tight">Performance</h1>
          <p className="text-sm text-muted-foreground mt-1">Individual salesperson metrics and leaderboard</p>
        </div>
        <ExportButton module="performance" params={{ zone, subzone, state, city, date_from: dateFrom, date_to: dateTo }} />
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3">
        <GeoFilters api={api} zone={zone} setZone={setZone} subzone={subzone} setSubzone={setSubzone} state={state} setState={setState} city={city} setCity={setCity} />
        <DateRangePicker dateFrom={dateFrom} dateTo={dateTo} onChange={(f, t) => { setDateFrom(f); setDateTo(t); }} />
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground">vs</span>
          <DateRangePicker dateFrom={baseFrom} dateTo={baseTo}
            onChange={(f, t) => { setBaseFrom(f); setBaseTo(t); }} />
        </div>
        {comparing && (
          <div className="flex items-center gap-1 text-xs">
            <span className="text-muted-foreground">Sort</span>
            <button onClick={() => setSortBy('amount')} data-testid="sort-amount"
              className={`px-2 py-1 rounded-md ${sortBy === 'amount' ? 'bg-primary text-primary-foreground' : 'hover:bg-accent'}`}>
              Amount
            </button>
            <button onClick={() => setSortBy('movers')} data-testid="sort-movers"
              className={`px-2 py-1 rounded-md ${sortBy === 'movers' ? 'bg-primary text-primary-foreground' : 'hover:bg-accent'}`}>
              Biggest movers
            </button>
          </div>
        )}
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input placeholder="Search name or user ID..." value={search} onChange={e => setSearch(e.target.value)}
            className="pl-8 w-56" data-testid="perf-search" />
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
        <div className="bg-card border border-border rounded-md p-4">
          <p className="kpi-label text-muted-foreground">Employees</p>
          <p className="text-xl font-heading font-bold mt-1">{k.total_employees || 0}</p>
        </div>
        <div className="bg-card border border-border rounded-md p-4">
          <p className="kpi-label text-muted-foreground">Total Orders</p>
          <p className="text-xl font-heading font-bold mt-1">{k.total_orders?.toLocaleString('en-IN') || 0}</p>
        </div>
        <div className="bg-card border border-border rounded-md p-4">
          <p className="kpi-label text-muted-foreground">Order Amount</p>
          <p className="text-xl font-heading font-bold mt-1">INR {formatINR(k.total_order_amount || 0)}</p>
          {comparing && (
            <p className="text-xs mt-1" data-testid="kpi-amount-base">
              <Delta now={k.total_order_amount} was={k.base?.total_order_amount}
                pct={k.change?.total_order_amount?.pct} />
              <span className="text-muted-foreground ml-1">vs {money(k.base?.total_order_amount)}</span>
            </p>
          )}
        </div>
        <div className="bg-card border border-border rounded-md p-4">
          <p className="kpi-label text-muted-foreground">Fulfilled</p>
          <p className="text-xl font-heading font-bold mt-1">INR {formatINR(k.total_fulfilled_amount || 0)}</p>
        </div>
        <div className="bg-card border border-border rounded-md p-4">
          <p className="kpi-label text-muted-foreground">Fulfilment Rate</p>
          <p className="text-xl font-heading font-bold mt-1">{k.avg_fulfilment_rate || 0}%</p>
        </div>
        <div className="bg-card border border-border rounded-md p-4">
          <p className="kpi-label text-muted-foreground">Outlets Covered</p>
          <p className="text-xl font-heading font-bold mt-1">{k.total_outlets_covered?.toLocaleString('en-IN') || 0}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Performers Chart */}
        <div className="bg-card border border-border rounded-md p-5">
          <h3 className="font-heading font-semibold text-sm mb-4 flex items-center gap-2">
            <Award size={16} strokeWidth={1.5} className="text-secondary" /> Top Performers
          </h3>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={topPerformers} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis type="number" tick={{ fontSize: 10 }} tickFormatter={formatINR} stroke="hsl(var(--muted-foreground))" />
                <YAxis type="category" dataKey="name" tick={{ fontSize: 9 }} width={130} stroke="hsl(var(--muted-foreground))" />
                <Tooltip content={<ChartTooltip />} />
                <Bar dataKey="order_amount" name="Order Amount" fill="#1B3A8C" radius={[0, 4, 4, 0]} />
                <Bar dataKey="fulfilled_amount" name="Fulfilled" fill="#B8974A" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Performance Table */}
        <div className="bg-card border border-border rounded-md p-5 overflow-auto">
          <h3 className="font-heading font-semibold text-sm mb-4 flex items-center gap-2">
            <Users size={16} strokeWidth={1.5} /> Leaderboard
          </h3>
          {loading ? <p className="text-sm text-muted-foreground">Loading...</p> : (
            <div className="max-h-80 overflow-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-left text-xs sticky top-0 bg-card">#</TableHead>
                    <TableHead className="text-left text-xs sticky top-0 bg-card">Employee</TableHead>
                    <TableHead className="text-right text-xs sticky top-0 bg-card">Orders</TableHead>
                    <TableHead className="text-right text-xs sticky top-0 bg-card">Amount</TableHead>
                    {comparing && <TableHead className="text-right text-xs sticky top-0 bg-card">Base</TableHead>}
                    {comparing && <TableHead className="text-right text-xs sticky top-0 bg-card">Change</TableHead>}
                    <TableHead className="text-right text-xs sticky top-0 bg-card">Ful %</TableHead>
                    <TableHead className="text-right text-xs sticky top-0 bg-card">Outlets</TableHead>
                    <TableHead className="text-right text-xs sticky top-0 bg-card">SKUs</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((row, i) => (
                    <TableRow key={i}>
                      <TableCell className="text-sm font-medium text-muted-foreground">{i + 1}</TableCell>
                      <TableCell className="text-sm">
                        <div>
                          <p className="font-medium">
                            {row.name || 'Unknown'}
                            {row.user_id && <span className="ml-1.5 text-xs font-normal text-muted-foreground">{row.user_id}</span>}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {row.reporting_to}
                            {row.state === 'gone' && <span className="ml-1.5 text-amber-600 dark:text-amber-400">nothing in this period</span>}
                            {row.state === 'new' && <span className="ml-1.5 text-emerald-600 dark:text-emerald-400">nothing in the base period</span>}
                          </p>
                        </div>
                      </TableCell>
                      <TableCell className="text-right text-sm">{row.total_orders?.toLocaleString('en-IN')}</TableCell>
                      <TableCell className="text-right text-sm">{formatINR(row.order_amount)}</TableCell>
                      {comparing && (
                        <TableCell className="text-right text-sm text-muted-foreground">
                          {formatINR(row.base?.order_amount || 0)}
                        </TableCell>
                      )}
                      {comparing && (
                        <TableCell className="text-right text-sm">
                          <Delta now={row.order_amount} was={row.base?.order_amount} />
                        </TableCell>
                      )}
                      <TableCell className="text-right text-sm">
                        <span className={row.fulfilment_rate >= 95 ? 'text-green-600 dark:text-green-400' : row.fulfilment_rate >= 90 ? 'text-yellow-600 dark:text-yellow-400' : 'text-red-600 dark:text-red-400'}>
                          {row.fulfilment_rate}%
                        </span>
                      </TableCell>
                      <TableCell className="text-right text-sm">{row.outlets_covered}</TableCell>
                      <TableCell className="text-right text-sm">{row.skus_sold}</TableCell>
                    </TableRow>
                  ))}
                  {(!data.employees || data.employees.length === 0) && (
                    <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-8">No performance data</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
